import { formaterJour, nomDuConnecteur, type VoiePiece } from '@admin-pmvb/core'
import { useQuery } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { type api, requetes } from '../api'
import { Logo } from '../composants/Logo'
import { cleLogoDeNom } from '../composants/marques'
import { Section, Vide } from '../composants/Section'

// Réglages, Connexions, « Fournisseurs » : comment arrivent les factures de chaque fournisseur,
// d'après celles reçues ces douze derniers mois (demande de Pierre-Marie du 20/10/2026). La voie
// principale est la plus fréquente ; « à la main » signale un fournisseur qu'un connecteur ou une
// consigne de tri (« le mail vaut pièce ») ferait entrer seul. Lecture seule, par entité.

type Reponse = InferResponseType<typeof api.portails.fournisseurs.$get, 200>
type Fournisseur = Reponse['fournisseurs'][number]

const VOIES: Record<VoiePiece, string> = {
  connecteur: 'Connecteur',
  mail_vaut_piece: 'Le mail vaut pièce',
  mail: 'Pièce jointe d’un mail',
  plateforme: 'Plateforme de facturation',
  assistant: 'Apportée par Claude',
  photo: 'Photo du téléphone',
  depot: 'Dépôt à la main',
}

const pluriel = (n: number, mot: string) => `${n} ${mot}${n > 1 ? 's' : ''}`

/** Le libellé d'une voie ; un connecteur dit lequel (« Connecteur Amazon »). */
function libelleVoie(voie: VoiePiece, f: Fournisseur): string {
  if (voie !== 'connecteur' || f.connecteurs.length === 0) return VOIES[voie]
  return `Connecteur ${f.connecteurs.map(nomDuConnecteur).join(', ')}`
}

/** Le logo d'un fournisseur : celui de son connecteur, sinon de son nom, sinon son initiale. */
const cleLogo = (f: Fournisseur) => f.connecteurs[0] ?? cleLogoDeNom(f.nom)

function LigneFournisseur({ f }: { f: Fournisseur }) {
  return (
    <div className="ligne">
      <div className="ligne-contenu">
        <div className="titre-logo">
          <Logo cle={cleLogo(f)} nom={f.nom} />
          <strong>{f.nom}</strong>
        </div>
        <span className={`badge ${f.aLaMain ? 'retard-en_retard' : ''}`}>
          {f.aLaMain
            ? `À la main (${VOIES[f.principale].toLowerCase()})`
            : libelleVoie(f.principale, f)}
        </span>
        <div className="discret">
          {pluriel(f.total, 'facture')}
          {f.derniere && ` · dernière le ${formaterJour(f.derniere)}`}
          {f.entites.length > 0 && ` · ${f.entites.join(', ')}`}
        </div>
        {f.voies.length > 1 && (
          <div className="discret">
            {f.voies.map((v) => `${libelleVoie(v.voie, f)} : ${v.nombre}`).join(' · ')}
          </div>
        )}
        {f.boites.length > 0 && (
          <div className="discret">
            {f.boites.length > 1 ? 'Boîtes' : 'Boîte'} : {f.boites.join(', ')}
          </div>
        )}
        {f.adresse && (
          <div className="discret">Pas de fiche fournisseur · envoyées par {f.adresse}</div>
        )}
      </div>
    </div>
  )
}

export function Fournisseurs() {
  const reponse = useQuery(requetes.voiesFournisseurs)
  const fournisseurs = reponse.data?.fournisseurs ?? []
  const compte = (voies: VoiePiece[]) =>
    fournisseurs.filter((f) => voies.includes(f.principale)).length
  return (
    <Section titre="Fournisseurs">
      <p className="discret">
        Comment arrivent les factures de chaque fournisseur, d’après celles reçues depuis le{' '}
        {reponse.data ? formaterJour(reponse.data.depuis) : '…'}. « À la main » : apportées par
        Claude, par une photo ou un dépôt ; un connecteur ou une consigne de tri (« le mail vaut
        pièce ») les ferait entrer seules.
      </p>
      {fournisseurs.length > 0 && (
        <p className="discret">
          {pluriel(fournisseurs.length, 'fournisseur')} · {compte(['connecteur'])} par connecteur ·{' '}
          {compte(['mail', 'mail_vaut_piece'])} par mail ·{' '}
          {compte(['plateforme']) > 0 && `${compte(['plateforme'])} par plateforme · `}
          {fournisseurs.filter((f) => f.aLaMain).length} à la main
        </p>
      )}
      {reponse.data && fournisseurs.length === 0 && <Vide>Aucune facture reçue depuis un an.</Vide>}
      {fournisseurs.map((f) => (
        <LigneFournisseur key={f.cle} f={f} />
      ))}
    </Section>
  )
}
